<template>
  <div>
    <v-list
      v-if="!loading"
      class="mt-1 list-tile-auto-height"
      dense
    >
      <!-- Link to (optional) TVTemplate and button to edit current template_data -->
      <v-list-tile v-if="!!spot.template">
        <v-icon color="msr">
          web
        </v-icon>
        <v-subheader class="auto-height">
          <v-layout
            wrap
            align-center
          >
            <span>{{ $t('tvtemplatesSpotFromTemplate') }}:&nbsp;</span>
            <router-link
              :to="{
                name: 'tvtemplate',
                params: {tvtemplateId: spot.template.id},
              }"
            >
              {{ spot.template.title }}
            </router-link>.&nbsp;
            <v-layout
              v-if="permissions.editTemplateData"
              align-center
              nowrap
            >
              <span>{{ $t('tvtemplatesSpotFromTemplateEdit') }}:</span>
              <v-btn
                icon
                small
                class="ml-0 my-0"
                data-cy="edit-template-data-btn"
                @click="$emit('edittemplatedata')"
              >
                <v-icon
                  small
                  color="msr"
                >
                  edit
                </v-icon>
              </v-btn>
            </v-layout>
          </v-layout>
        </v-subheader>
      </v-list-tile>
      <!-- Tell the User if this Content is (not yet) used in all Stores -->
      <v-list-tile v-if="spot.published && isUsedInAllStores">
        <v-icon color="success">
          check
        </v-icon>
        <v-subheader>{{ $t('contentUsedOnAllSites') }}</v-subheader>
      </v-list-tile>
      <v-list-tile
        v-if="spot.published && !isUsedInAllStores && spot.usage_in_stores"
      >
        <v-icon color="blue">
          info
        </v-icon>
        <v-menu max-height="75vh">
          <v-subheader
            slot="activator"
            class="pseudo-anchor--inverted"
          >
            {{ contentUsedOnSitesHint }}
          </v-subheader>
          <v-list class="list-tile-auto-height">
            <v-list-tile class="unselectable py-1">
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ contentUsedOnSitesHint }}
                </v-list-tile-title>
                <v-list-tile-sub-title
                  class="mb-1"
                  style="
                    font-size: smaller;
                    max-width: 400px;
                    white-space: normal;
                    height: auto;
                  "
                >
                  {{ $t('contentStoreUsageStatisticsChart_2_appendix') }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-divider v-if="storesFromUsage.length > 0" />
            <v-list-tile
              v-for="store in storesFromUsage"
              :key="store.id"
              class="store-link-tile"
              :to="{name: 'sites', query: {search: store.city}}"
            >
              <v-layout
                class="body-1 py-1"
                align-center
              >
                <v-icon
                  medium
                  class="mr-3 unselectable"
                  :color="store._offline ? 'error' : 'success'"
                >
                  location_on
                </v-icon>
                <div class="store-link-tile--text">
                  <small class="d-block grey--text text--darken-2">{{
                    store.name
                  }}</small>
                  <strong>{{ store.city }}</strong>
                </div>
              </v-layout>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-list-tile>
      <!-- Last shared to Facebook page -->
      <v-list-tile v-if="spot.last_shared_on_facebook_page">
        <img
          src="/static/icons8-facebook-96.png"
          width="22px"
        >
        <v-subheader>
          <v-layout wrap>
            {{ $t('sharedToFacebookPageLast') }}&nbsp;
            <div>
              <a :href="spot.last_shared_on_facebook_page.page_link">{{
                spot.last_shared_on_facebook_page.page_name
              }}</a>
              {{ $t('on') }}
              {{
                spot.last_shared_on_facebook_page.timestamp
                  | dayjs('DD.MM.YYYY')
              }}
            </div>
          </v-layout>
        </v-subheader>
      </v-list-tile>
      <!-- Last shared to Instagram -->
      <v-list-tile v-if="spot.last_shared_on_instagram">
        <img
          src="/static/instagram-96.png"
          width="22px"
        >
        <v-subheader>
          <v-layout wrap>
            <a :href="spot.last_shared_on_instagram.permalink">
              {{ $t('sharedToInstagramLast') }}</a>&nbsp;{{ $t('on') }}
            {{ spot.last_shared_on_instagram.timestamp | dayjs('DD.MM.YYYY') }}
          </v-layout>
        </v-subheader>
      </v-list-tile>
      <!-- Hard Start/Expiration Warnings -->
      <hard-range-warning-list-tile
        :hard-start="spot.hard_start"
        :hard-expire="spot.hard_expire"
        :chosen-period-feedback="false"
      >
        <div
          slot="extra"
          class="nowrap"
        >
          <!-- Shortcut button to edit Hard Range -->
          <v-btn
            v-if="permissions.editHardRange"
            icon
            class="mr-0"
            small
            :title="$t('contentHardRangeSet')"
            @click="$emit('edithardrange')"
          >
            <v-icon
              color="warning"
              small
            >
              edit
            </v-icon>
          </v-btn>
          <!-- Help button to explain Hard Range -->
          <v-btn
            icon
            small
            :class="{'ml-0': permissions.editHardRange}"
            @click="showHardRangeTooltipInDialog()"
          >
            <v-icon
              color="grey"
              small
            >
              help_outline
            </v-icon>
          </v-btn>
        </div>
      </hard-range-warning-list-tile>
      <!-- Expired -->
      <v-list-tile
        v-if="spot.expired"
        data-cy="spot-detail-status-expired-warning"
      >
        <v-icon color="warning">
          warning
        </v-icon>
        <v-subheader class="warning--text">
          {{ $t('expiredContent') }}
        </v-subheader>
      </v-list-tile>
      <v-list-tile
        v-if="
          hasOverlayOptions &&
            !(
              overlayOptionsAreGrouped && overlayOptionsVisibleGroups.length === 0
            )
        "
        class="list-tile-min-height list-tile-auto-height"
      >
        <v-icon color="msr">
          edit
        </v-icon>
        <v-subheader class="auto-height">
          {{ $t('contentHasOverlayOptions') }}
        </v-subheader>
      </v-list-tile>
      <!-- Unpublished warning, 'publish now?' -->
      <v-list-tile
        v-if="!spot.published"
        class="list-tile-min-height list-tile-auto-height"
      >
        <unpublished-indicator
          is-unpublished
          :use-default-margins="false"
        />
        <v-subheader class="auto-height">
          <v-layout
            align-center
            wrap
          >
            <div class="mr-1">
              {{ $t('contentNotPublished') }}
              <span v-if="contentWillBeAutopublished">
                ({{ $t('contentNotPublishedAutoPublish') }}
                <span class="warning--text">{{
                  spot.hard_start | dayjs('DD.MM.YYYY')
                }}</span>)
              </span>
            </div>
            <div
              v-if="permissions.publish"
              class="ml-2"
            >
              <a
                href="javascript:void(0)"
                data-cy="anchor-publish-content-now"
                @click="$emit('publish')"
              >{{ $t('publishContentNow') }}</a>
            </div>
            <!-- Help button to explain Publish -->
            <v-btn
              icon
              small
              @click="showPublishTooltipInDialog()"
            >
              <v-icon
                color="grey"
                small
              >
                help_outline
              </v-icon>
            </v-btn>
          </v-layout>
        </v-subheader>
      </v-list-tile>
    </v-list>
    <!-- Selected Timeframe Filter -->
    <selected-timeframe-chip v-if="spot.id" />
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import overlayOptions from '@/mixins/overlayOptions.js';

import HardRangeWarningListTile from '@/components/HardRangeWarningListTile';
import SelectedTimeframeChip from '@/components/SelectedTimeframeChip';
import UnpublishedIndicator from '@/components/UnpublishedIndicator';

export default {
  components: {
    HardRangeWarningListTile,
    SelectedTimeframeChip,
    UnpublishedIndicator,
  },
  mixins: [overlayOptions],
  props: {
    spot: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    permissions: {
      type: Object,
      required: true,
    },
  },
  computed: {
    storesFromUsage() {
      if (!this.spot || !this.spot.usage_in_stores) {
        return [];
      }

      const storesWithUsage = [];
      for (const row of Object.values(this.spot.usage_in_stores.usage)) {
        storesWithUsage.push(row.store);
      }

      const storesWithoutUsage = [];
      for (const row of Object.values(this.spot.usage_in_stores.no_usage)) {
        row.store._offline = true;
        storesWithoutUsage.push(row.store);
      }

      const stores = [
        ...orderBy(storesWithUsage, ['city', 'name']),
        ...orderBy(storesWithoutUsage, ['city', 'name']),
      ];

      return stores;
    },
    contentWillBeAutopublished() {
      return this.spot && !this.spot.published && this.spot.hard_start;
    },
    contentUsedOnSitesHint() {
      if (!this.spot.usage_in_stores) {
        return '';
      }
      let template = this.$t('contentUsedOnSites');
      const numUsed = this.spot.usage_in_stores.num_stores_running_spot;
      if (numUsed === 0) {
        return this.$t('contentUsedOnNoSites');
      }
      const numAll = this.spot.usage_in_stores.num_stores;
      template = template.replace('{{ numUsed }}', numUsed);
      template = template.replace('{{ numAll }}', numAll);
      return template;
    },
    isUsedInAllStores() {
      if (!this.spot.usage_in_stores) {
        return false;
      }
      if (
        this.spot.usage_in_stores.num_stores_running_spot === 0 &&
        this.spot.usage_in_stores.num_stores === 0
      ) {
        return false;
      }
      return (
        this.spot.usage_in_stores.num_stores_running_spot ===
        this.spot.usage_in_stores.num_stores
      );
    },
  },
  methods: {
    showHardRangeTooltipInDialog() {
      this.$bus.$emit('show-dynamic-dialog', {
        title: this.$t('contentHardRangeTerm'),
        titleIcon: 'help_outline',
        titleIconClass: 'info--text',
        content: `
					<div class="subheading">
						${this.$t('contentHardRangeTooltip')}
					</div>
				`,
      });
    },
    showPublishTooltipInDialog() {
      this.$bus.$emit('show-dynamic-dialog', {
        title: this.$t('publishContentVisibility'),
        titleIcon: 'help_outline',
        titleIconClass: 'info--text',
        content: `
					<div class="subheading">
						${this.$t('publishContentVisibilityHint')}
					</div>
				`,
      });
    },
  },
};
</script>

<style scoped>
.store-link-tile:hover .store-link-tile--text {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/SpotDetail/SpotDetailStatus.vue